import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, CalendarClock, AlertTriangle, CheckCircle2, Send, RefreshCw, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatDate, LIFE_EVENTS } from "@/lib/constants";

/**
 * Stage 5 · Ongoing Review tab.
 *
 * Reviews come in two flavours:
 *   • Scheduled Annual — auto-created 12 months after the last plan is executed
 *     and again every time an annual review is completed.
 *   • Life Event — created manually by staff when they log an event on a client.
 *
 * This tab surfaces overdue vs upcoming reviews separately, lets staff:
 *   - reschedule the due date
 *   - send a "gentle reminder" to the client (real email + mocked SMS)
 *   - mark the review completed WITH outcome notes
 *   - spawn a new plan version from a completed review (deep-links to Plan tab)
 */

const daysBetween = (a, b) => Math.floor((new Date(a) - new Date(b)) / (86400 * 1000));

export default function ReviewsTab({ client, canEdit, reload }) {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [event, setEvent] = useState(LIFE_EVENTS[0]);
  const [notes, setNotes] = useState("");
  const [rescheduleFor, setRescheduleFor] = useState(null); // review being rescheduled
  const [newDueDate, setNewDueDate] = useState("");
  const [completeFor, setCompleteFor] = useState(null); // review being completed
  const [outcomeNotes, setOutcomeNotes] = useState("");
  const [spawnPlan, setSpawnPlan] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/clients/${client.id}/reviews`);
    setReviews(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client.id]);

  const logEvent = async () => {
    try {
      await api.post(`/clients/${client.id}/life-event`, { life_event_type: event, notes });
      toast.success("Life event logged — review scheduled");
      setDlgOpen(false); setNotes("");
      load(); reload && reload();
    } catch { toast.error("Failed"); }
  };

  const reschedule = async () => {
    if (!rescheduleFor || !newDueDate) return;
    try {
      await api.patch(`/reviews/${rescheduleFor.id}`, { due_date: new Date(newDueDate).toISOString() });
      toast.success("Review rescheduled");
      setRescheduleFor(null); setNewDueDate("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const sendReminder = async (r) => {
    try {
      const { data } = await api.post(`/reviews/${r.id}/send-reminder`);
      toast.success(`Reminder sent · ${(data.channels || []).join(", ") || "logged"}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const complete = async () => {
    if (!completeFor) return;
    try {
      const { data } = await api.patch(`/reviews/${completeFor.id}`, {
        completed: true,
        outcome_notes: outcomeNotes || "Reviewed",
        spawn_next_annual: completeFor.trigger_type === "Scheduled Annual",
        spawn_plan_draft: spawnPlan,
      });
      toast.success("Review completed" + (data.next_review_id ? " · next annual scheduled" : ""));
      setCompleteFor(null); setOutcomeNotes(""); setSpawnPlan(false);
      load(); reload && reload();
      if (data.new_plan_id) {
        toast.success("Draft plan version created — open the Written Plan tab.");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const now = new Date();
  const overdue = reviews.filter((r) => !r.completed_at && new Date(r.due_date) < now);
  const upcoming = reviews.filter((r) => !r.completed_at && new Date(r.due_date) >= now);
  const completed = reviews.filter((r) => r.completed_at);
  const nextScheduled = upcoming.find((r) => r.trigger_type === "Scheduled Annual");

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="overline mb-1 text-primary">Stage 5 · Ongoing Review</p>
          <h3 className="font-dashboard text-lg font-bold">Reviews for {client.full_name?.split(" ")[0]}</h3>
          {nextScheduled ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Next annual review due <b>{formatDate(nextScheduled.due_date)}</b>
              {" · "}
              <span className={new Date(nextScheduled.due_date) < now ? "text-destructive" : ""}>
                {daysBetween(nextScheduled.due_date, now) >= 0 ? `${daysBetween(nextScheduled.due_date, now)} days away` : `${Math.abs(daysBetween(nextScheduled.due_date, now))} days overdue`}
              </span>
            </p>
          ) : completed.length > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">No annual review currently scheduled.</p>
          )}
        </div>
        {canEdit && (
          <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="log-life-event-btn"><Plus className="mr-1 h-3 w-3" /> Log life event</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Log a life event</DialogTitle></DialogHeader>
              <div className="space-y-3 py-3">
                <div>
                  <Label>Event</Label>
                  <select className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={event} onChange={(e) => setEvent(e.target.value)} data-testid="life-event-select">
                    {LIFE_EVENTS.map((e) => <option key={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="life-event-notes" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={logEvent} data-testid="life-event-save">Log event & schedule review</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {reviews.length === 0 && <p className="text-sm text-muted-foreground">No reviews on record.</p>}

      {overdue.length > 0 && (
        <ReviewSection
          title="Overdue"
          Icon={AlertTriangle}
          tone="danger"
          reviews={overdue}
          now={now}
          canEdit={canEdit}
          onReschedule={(r) => { setRescheduleFor(r); setNewDueDate(r.due_date?.slice(0, 10) || ""); }}
          onRemind={sendReminder}
          onComplete={(r) => setCompleteFor(r)}
        />
      )}

      {upcoming.length > 0 && (
        <ReviewSection
          title="Upcoming"
          Icon={CalendarClock}
          tone="primary"
          reviews={upcoming}
          now={now}
          canEdit={canEdit}
          onReschedule={(r) => { setRescheduleFor(r); setNewDueDate(r.due_date?.slice(0, 10) || ""); }}
          onRemind={sendReminder}
          onComplete={(r) => setCompleteFor(r)}
        />
      )}

      {completed.length > 0 && (
        <section data-testid="reviews-completed-section">
          <div className="mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            <h4 className="font-dashboard text-sm font-bold">Completed</h4>
            <span className="text-xs text-muted-foreground">({completed.length})</span>
          </div>
          <div className="space-y-2">
            {completed.map((r) => (
              <Card key={r.id} className="border-border bg-white p-4 text-sm" data-testid={`review-${r.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 font-medium">
                      {r.trigger_type}
                      {r.life_event_type && <span className="text-xs text-muted-foreground">· {r.life_event_type}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">Completed {formatDate(r.completed_at)}</div>
                    {r.outcome_notes && <p className="mt-2 text-sm">{r.outcome_notes}</p>}
                    {r.new_plan_id && (
                      <button
                        onClick={() => navigate(`/app/clients/${client.id}?tab=plan`)}
                        className="mt-2 inline-flex items-center gap-1 text-xs text-primary underline"
                        data-testid={`review-linked-plan-${r.id}`}
                      >
                        <Sparkles className="h-3 w-3" /> Linked draft plan version
                      </button>
                    )}
                  </div>
                  <span className="stage-chip stage-5">Done</span>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Reschedule dialog */}
      <Dialog open={!!rescheduleFor} onOpenChange={(o) => !o && setRescheduleFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reschedule review</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <div className="rounded-md bg-secondary/40 p-3 text-xs">
              <div className="font-medium">{rescheduleFor?.trigger_type}</div>
              {rescheduleFor?.life_event_type && <div className="text-muted-foreground">Event · {rescheduleFor.life_event_type}</div>}
              <div className="text-muted-foreground">Currently due · {formatDate(rescheduleFor?.due_date)}</div>
            </div>
            <div>
              <Label>New due date</Label>
              <Input type="date" value={newDueDate} onChange={(e) => setNewDueDate(e.target.value)} data-testid="review-new-due-date" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={reschedule} disabled={!newDueDate} data-testid="review-reschedule-save">Save new date</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete dialog */}
      <Dialog open={!!completeFor} onOpenChange={(o) => !o && setCompleteFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Complete review</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <div className="rounded-md bg-secondary/40 p-3 text-xs">
              <div className="font-medium">{completeFor?.trigger_type}</div>
              {completeFor?.life_event_type && <div className="text-muted-foreground">Event · {completeFor.life_event_type}</div>}
              <div className="text-muted-foreground">Was due · {formatDate(completeFor?.due_date)}</div>
            </div>
            <div>
              <Label>Outcome notes</Label>
              <Textarea
                rows={4}
                value={outcomeNotes}
                onChange={(e) => setOutcomeNotes(e.target.value)}
                placeholder="What did we discuss? Any changes to goals or risk?"
                data-testid="review-outcome-notes"
              />
            </div>
            <label className="flex items-start gap-2 rounded-md border border-border bg-white p-3 text-xs">
              <input
                type="checkbox"
                checked={spawnPlan}
                onChange={(e) => setSpawnPlan(e.target.checked)}
                className="mt-0.5"
                data-testid="review-spawn-plan-checkbox"
              />
              <span>
                <b>Spawn a new draft plan version</b> — useful when goals or risk have shifted meaningfully.
                A new version will inherit the current active advice items and land in the Written Plan tab.
              </span>
            </label>
            {completeFor?.trigger_type === "Scheduled Annual" && (
              <p className="text-xs text-muted-foreground">
                Completing this will automatically schedule the next annual review 12 months from today.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={complete} data-testid="review-complete-save">Mark completed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReviewSection({ title, Icon, tone, reviews, now, canEdit, onReschedule, onRemind, onComplete }) {
  const toneClass = tone === "danger" ? "text-destructive" : "text-primary";
  return (
    <section data-testid={`reviews-${title.toLowerCase()}-section`}>
      <div className="mb-3 flex items-center gap-2">
        <Icon className={`h-4 w-4 ${toneClass}`} />
        <h4 className="font-dashboard text-sm font-bold">{title}</h4>
        <span className="text-xs text-muted-foreground">({reviews.length})</span>
      </div>
      <div className="space-y-3">
        {reviews.map((r) => {
          const diff = daysBetween(r.due_date, now);
          const dueLabel = tone === "danger"
            ? `${Math.abs(diff)} days overdue`
            : diff === 0 ? "Due today" : `${diff} days away`;
          return (
            <Card key={r.id} className={`border-l-4 ${tone === "danger" ? "border-l-destructive" : "border-l-primary"} border-border bg-white p-4`} data-testid={`review-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-dashboard text-sm font-bold">{r.trigger_type}</span>
                    {r.life_event_type && (
                      <span className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-medium text-accent">
                        {r.life_event_type}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Due {formatDate(r.due_date)} · <span className={toneClass}>{dueLabel}</span>
                  </div>
                  {r.outcome_notes && <p className="mt-2 text-sm">{r.outcome_notes}</p>}
                </div>
                {canEdit && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => onReschedule(r)} className="gap-1" data-testid={`reschedule-review-${r.id}`}>
                      <RefreshCw className="h-3 w-3" /> Reschedule
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onRemind(r)} className="gap-1" data-testid={`remind-review-${r.id}`}>
                      <Send className="h-3 w-3" /> Send reminder
                    </Button>
                    <Button size="sm" onClick={() => onComplete(r)} data-testid={`complete-review-${r.id}`}>
                      Complete
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </section>
  );
}
